import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../../hooks/useLang';
import { useLogin } from '../../hooks/useLogin';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faBookOpen, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import '../../styles/auth/Login.scss';

const Login = () => {
    const { urlLanguage } = useLang();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const { login, error, isLoading } = useLogin();
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const result = await login(email, password);
        if (result && result.success) {
            const user = result.json.user;
            const hasCompletedOnboarding = !!(user && user.status);

            if (!hasCompletedOnboarding) {
                navigate(urlLanguage('/onboarding'));
            } else {
                navigate(urlLanguage('/'));
            }
        }
    };

    return (
        <div className="login-page">
            {/* ── Left decorative panel ── */}
            <div className="auth-left-panel">
                {/* Decorative rings */}
                <div className="panel-ring panel-ring--1" />
                <div className="panel-ring panel-ring--2" />
                <div className="panel-ring panel-ring--3" />
                <div className="panel-ring panel-ring--4" />
                <div className="panel-glow" />

                <div className="panel-content">
                    <div className="panel-wordmark">YeniDers</div>
                    <p className="panel-tagline">
                        Where students come to<br />
                        <em>learn, share & grow.</em>
                    </p>
                    <div className="panel-divider" />
                    <div className="panel-stats">
                        <div className="panel-stat">
                            <span className="stat-number">50K+</span>
                            <span className="stat-label">Students</span>
                        </div>
                        <div className="panel-stat">
                            <span className="stat-number">200K+</span>
                            <span className="stat-label">Notes shared</span>
                        </div>
                        <div className="panel-stat">
                            <span className="stat-number">98%</span>
                            <span className="stat-label">Satisfaction</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Right form panel ── */}
            <div className="auth-right-panel">
                <div className="auth-form-wrap">
                    {/* Mobile-only logo */}
                    <div className="auth-mobile-logo">
                        <div className="auth-logo-icon-wrap">
                            <FontAwesomeIcon icon={faBookOpen} />
                        </div>
                        YeniDers
                    </div>

                    <Link to="/" className="back-to-home-link">
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to homepage
                    </Link>

                    <div className="auth-header">
                        <h1>Welcome back 👋</h1>
                        <p>Log in to your account to continue learning.</p>
                    </div>

                    <div className="auth-card">
                        <form onSubmit={handleSubmit}>
                            {error && (
                                <div className="auth-error">{error}</div>
                            )}

                            <div className="auth-form-group">
                                <label>Email address</label>
                                <input
                                    type="email"
                                    value={email}
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="auth-form-group password-group">
                                <label>Password</label>
                                <div className="input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        placeholder="Your password"
                                        autoComplete="current-password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <FontAwesomeIcon
                                        className="password-toggle-icon"
                                        icon={showPassword ? faEyeSlash : faEye}
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                </div>
                            </div>

                            <Link className="forgot-password-link" to={urlLanguage('/forgot-password')}>
                                Forgot password?
                            </Link>

                            <button className="auth-submit-btn" disabled={isLoading}>
                                {isLoading ? 'Logging in…' : 'Log In'}
                            </button>
                        </form>

                        <div className="auth-footer">
                            Don't have an account?
                            <Link to={urlLanguage('/signup')}>Create an account</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
